/* global fetch */

import { MediaItem } from '@birdi/types';
import { config } from '../config';
import {
  headers,
  APIResponse,
  stleaf,
  fetchAPI,
  SpanTag,
  APIError,
} from '../helpers';
import { OrganisationTag } from './organisation';

export const getOne = (
  st: SpanTag,
  mediaId: string,
): Promise<APIResponse<MediaItem>> => {
  const url = `${config.API_URL}/v2/media/${mediaId}`;
  return fetchAPI(stleaf(st, 'yu2zu8'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export const get360 = (st: SpanTag, mediaId: string): Promise<APIResponse> => {
  const url = `${config.API_URL}/v2/media/${mediaId}/360`;
  return fetchAPI(stleaf(st, 'xeec8u'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export const getSignedUrl = (
  st: SpanTag,
  mediaId: string,
): Promise<APIResponse> => {
  const url = `${config.API_URL}/v2/media/${mediaId}/signedurl`;
  return fetchAPI(stleaf(st, 'yu2zu8'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export const getVideoMetaData = (
  st: SpanTag,
  mediaId: string,
): Promise<APIResponse> => {
  const url = `${config.API_URL}/v2/media/${mediaId}/video`;
  return fetchAPI(stleaf(st, 'vidvu8'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export const getOriginal = (
  st: SpanTag,
  mediaId: string,
): Promise<APIResponse<{ id: string; originalUrl: string }>> => {
  const url = `${config.API_URL}/v1/media/${mediaId}/original`;
  return fetchAPI(stleaf(st, 'ahce6m'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export const getPreview = (
  st: SpanTag,
  mediaId: string,
): Promise<APIResponse> => {
  const url = `${config.API_URL}/v2/media/${mediaId}/preview`;
  return fetchAPI(stleaf(st, 'we9ef4'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

interface FieldsOpts {
  title: string;
  downloadCount: number;
  // the rest?! =(
}
export const update = (
  st: SpanTag,
  mediaId: string,
  fields: Partial<FieldsOpts>,
): Promise<APIResponse> => {
  const url = `${config.API_URL}/v2/media/${mediaId}`;
  return fetchAPI(stleaf(st, 'eis3ji'), url, {
    method: 'PUT',
    credentials: 'include',
    headers,
    body: JSON.stringify(fields),
  });
};

export const updateTags = (
  st: SpanTag,
  mediaId: string,
  tags: Array<string>,
): Promise<APIResponse> => {
  const url = `${config.API_URL}/v2/media/${mediaId}/tags`;
  return fetchAPI(stleaf(st, 'ohyu4k'), url, {
    method: 'PUT',
    credentials: 'include',
    headers,
    body: JSON.stringify(tags),
  });
};

export const bulkUpdateTags = (
  st: SpanTag,
  mediaIds: Array<string>,
  tags: Array<string>,
): Promise<APIResponse> => {
  const url = `${config.API_URL}/v1/media/tags/bulk-update`;
  return fetchAPI(stleaf(st, 'lie6oo'), url, {
    method: 'PUT',
    credentials: 'include',
    headers,
    body: JSON.stringify({ mediaIds, tags }),
  });
};

export const checkTagsEquality = (
  st: SpanTag,
  mediaIds: Array<string>,
): Promise<APIResponse> => {
  const url = `${config.API_URL}/v2/media/tags/check-equality`;
  return fetchAPI(stleaf(st, 'ze0ahd'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({ mediaIds }),
  });
};

export const getTags = (st: SpanTag, mediaId: string): Promise<APIResponse> => {
  const url = `${config.API_URL}/v2/media/${mediaId}/tags`;
  return fetchAPI(stleaf(st, 'ohwue3'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export const provisionDownload = (
  st: SpanTag,
  name: string,
  arrayOfFiles: Array<string>,
): Promise<APIResponse<{ uuid: string }>> => {
  const url = `${config.S3_ZIP_URL}`;
  return fetchAPI(stleaf(st, 'zai9oh'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({ name, items: arrayOfFiles }),
  });
};

export const requestZipDownload = (
  st: SpanTag,
  arrayOfFiles: Array<string>,
): Promise<APIResponse> => {
  const url = `${config.API_URL}/v2/zip-mail`;
  return fetchAPI(stleaf(st, 'aenuu1'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({ sources: arrayOfFiles }),
  });
};

export const requestZipDownloadMediaIds = (
  st: SpanTag,
  mediaIds: Array<string>,
): Promise<APIResponse> => {
  const url = `${config.API_URL}/v2/zip-mail/media`;
  return fetchAPI(stleaf(st, 'bi9eic'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({ mediaIds }),
  });
};

export const getDownloadLink = (downloadId: string): string =>
  `${config.S3_ZIP_URL}/${downloadId}`;

export const addComment = (
  st: SpanTag,
  mediaId: string,
  comment,
): Promise<APIResponse> => {
  const url = `${config.API_URL}/v1/media/${mediaId}/comments`;
  return fetchAPI(stleaf(st, 'phie2z'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(comment),
  });
};

export const getComment = (
  st: SpanTag,
  mediaId: string,
): Promise<APIResponse> => {
  const url = new URL(`${config.API_URL}/v1/media/${mediaId}/comments`);
  return fetchAPI(stleaf(st, 'aipi5j'), url.toString(), {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export const deleteComment = (
  st: SpanTag,
  commentId: string,
): Promise<APIResponse> => {
  const url = `${config.API_URL}/v1/media/comments/${commentId}`;
  return fetchAPI(stleaf(st, 'ageg4a'), url, {
    method: 'DELETE',
    credentials: 'include',
    headers,
  });
};

enum ReviewStatus {
  Neutral = 'neutral',
  Accepted = 'accepted',
  Rejected = 'rejected',
}

export const postQA = (
  st: SpanTag,
  mediaId: string,
  { status, notes }: { status: ReviewStatus; notes?: string },
): Promise<APIResponse> => {
  const url = `${config.API_URL}/v1/media/${mediaId}/qa`;
  return fetchAPI(stleaf(st, 'vei7aj'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({ status, notes }),
  });
};

export const getQA = (st: SpanTag, mediaId: string): Promise<APIResponse> => {
  const url = `${config.API_URL}/v1/media/${mediaId}/qa`;
  return fetchAPI(stleaf(st, 'ahz4ve'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export type ExifData = {
  Orientation?: number;
  ImageWidth: number;
  ImageHeight: number;
};

export const getExifData = (
  st: SpanTag,
  mediaId: string,
  signal?: AbortSignal,
): Promise<APIResponse> => {
  const url = `${config.API_URL}/v2/media/${mediaId}/exif/v2`;
  const res = fetchAPI(stleaf(st, 'aebei6'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
    signal,
  });
  return res;
};

export const getExifDataUrl = (mediaId: string) =>
  `${config.API_URL}/v2/media/${mediaId}/exif/v2`;

export const updateLocation = (st: SpanTag, mediaId: string, location: any) => {
  const url = `${config.API_URL}/v2/media/${mediaId}/update-location`;
  return fetchAPI(stleaf(st, 'iof3zo'), url, {
    method: 'PUT',
    credentials: 'include',
    headers,
    body: JSON.stringify({ location }),
  });
};

export const updateDateTaken = (
  st: SpanTag,
  mediaId: string,
  dateTaken: any,
) => {
  const url = `${config.API_URL}/v2/media/${mediaId}/update-datetaken`;
  return fetchAPI(stleaf(st, 'ufex5k'), url, {
    method: 'PUT',
    credentials: 'include',
    headers,
    body: JSON.stringify({ dateTaken }),
  });
};

/**
 * Set (or clear, with null) the survey date on a media item -- when the data
 * was captured, as opposed to when it was uploaded. Map-scoped: the caller
 * needs `map:edit` on a map the media appears on.
 */
export const updateSurveyedAt = (
  st: SpanTag,
  mediaId: string,
  mapId: string,
  surveyedAt: Date | null,
) => {
  const url = `${config.API_URL}/v2/media/${mediaId}/surveyed-at`;
  return fetchAPI(stleaf(st, 'sokoo8'), url, {
    method: 'PUT',
    credentials: 'include',
    headers,
    body: JSON.stringify({
      mapId,
      surveyedAt: surveyedAt ? surveyedAt.toISOString() : null,
    }),
  });
};

// TODO: what are params?
export const processFilePath = (
  st: SpanTag,
  mediaId: string,
  params: any,
): Promise<APIResponse> => {
  const url = `${config.API_URL}/v2/media/${mediaId}/video/flight-path`;
  return fetchAPI(stleaf(st, 'kahth3'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify(params),
  });
};

export const updateMediaZPosition = (
  st: SpanTag,
  params: { id: string; zPosition: number }[],
) => {
  const url = `${config.API_URL}/v2/media/z-position`;
  return fetchAPI(stleaf(st, 'bi2ooy'), url, {
    method: 'PUT',
    credentials: 'include',
    headers,
    body: JSON.stringify(params),
  });
};

export const generatePointcloud = (missionId: string, mediaId: string) => {
  const url = `${config.POINTS_URL}/generate/v0/${missionId}/media/${mediaId}`;
  return fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers,
  })
    .then((r) => r.json())
    .catch((r) => console.log(r))
    .then((r: { code: number; message: string }) => {
      if (r.code !== 200) {
        throw new Error(r.message);
      } else {
        return r;
      }
    });
};

export const open3DView = (missionId: string, mediaId: string) => {
  const url = `${config.POINTS_URL}/map/v0/${missionId}/media/${mediaId}`;
  window.location.href = url;
};

export const generatePointcloudV2 = (
  mapId: string,
  mediaId: string,
): Promise<{ code: number; message: string; redirectUrl?: string }> => {
  return new Promise((resolve, reject) => {
    // Step 1: Start generation
    const generateUrl = `${config.POINTS_URL}/generate/v2/${mapId}/media/${mediaId}`;
    fetch(generateUrl, {
      method: 'GET',
      credentials: 'include',
      headers,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        return response.json();
      })
      .then((result: { code: number; message: string }) => {
        if (result.code !== 200) {
          throw new Error(result.message);
        }

        // Step 2: Monitor via SSE until completion
        const statusUrl = `${config.POINTS_URL}/status/v2/${mapId}/media/${mediaId}`;
        const eventSource = new EventSource(statusUrl, {
          withCredentials: true,
        });

        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);

            if (data.status === 'complete') {
              eventSource.close();
              resolve({
                code: data.code || 200,
                message: data.message,
                redirectUrl: data.redirectUrl,
              });
            } else if (data.status === 'error') {
              eventSource.close();
              reject(new Error(data.message));
            }
            // For other statuses (generating, etc.), just continue monitoring
          } catch (error) {
            eventSource.close();
            reject(new Error('Failed to parse server response'));
          }
        };

        eventSource.onerror = (error) => {
          eventSource.close();
          reject(new Error('Connection to server lost or timed out'));
        };

        // Cleanup on timeout (30 minutes)
        setTimeout(
          () => {
            if (eventSource.readyState !== EventSource.CLOSED) {
              eventSource.close();
              reject(new Error('Request timed out after 30 minutes'));
            }
          },
          30 * 60 * 1000,
        );
      })
      .catch((error) => {
        console.error('Pointcloud generation error:', error);
        reject(
          new Error(
            error.message || 'Network timeout or connection error occurred',
          ),
        );
      });
  });
};

export const open3DViewV2 = (mapId: string, mediaId: string, title: string) => {
  const url = `${config.POINTS_URL}/map/v2/${mapId}/media/${mediaId}?title=${title}`;
  window.location.href = url;
};

export const updateComment = (
  st: SpanTag,
  mediaId: string,
  comments: string,
): Promise<APIResponse<{ comments: string }>> => {
  const url = `${config.API_URL}/v2/media/${mediaId}/comments`;
  return fetchAPI(stleaf(st, 'aer6fe'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({ comments }),
  });
};

export const bulkUpdateComments = (
  st: SpanTag,
  mediaIds: string[],
  comments: string,
): Promise<APIResponse<{ comments: string }>> => {
  const url = `${config.API_URL}/v2/media/comments/bulk`;
  return fetchAPI(stleaf(st, 'ua2ato'), url, {
    method: 'PUT',
    credentials: 'include',
    headers,
    body: JSON.stringify({ mediaIds, comments }),
  });
};

export const updateTagsV2 = (
  st: SpanTag,
  mediaId: string,
  tagIds: string[],
): Promise<APIResponse<Pick<OrganisationTag, 'id' | 'name'>[]>> => {
  const url = `${config.API_URL}/v2/media/${mediaId}/tags/v2`;
  return fetchAPI(stleaf(st, 'eng5ei'), url, {
    method: 'PUT',
    credentials: 'include',
    headers,
    body: JSON.stringify(tagIds),
  });
};

export const bulkUpdateTagsV2 = (
  st: SpanTag,
  mediaIds: string[],
  tagIds: string[],
): Promise<APIResponse<Pick<OrganisationTag, 'id' | 'name'>[]>> => {
  const url = `${config.API_URL}/v2/media/tags/v2/bulk`;
  return fetchAPI(stleaf(st, 'eixe4r'), url, {
    method: 'PUT',
    credentials: 'include',
    headers,
    body: JSON.stringify({ mediaIds, tags: tagIds }),
  });
};

interface MediaExif {
  mediaId: string;
  exif: Record<string, any>;
}

export const getExifV2 = (
  st: SpanTag,
  mediaId: string,
): Promise<APIResponse<MediaExif['exif']>> => {
  const url = `${config.API_URL}/v2/media/${mediaId}/exif/v2`;
  return fetchAPI(stleaf(st, 'aj5eij'), url, {
    method: 'GET',
    credentials: 'include',
    headers,
  });
};

export const getExif2Url = (mediaId: string) =>
  `${config.API_URL}/v2/media/${mediaId}/exif/v2`;

export const getExifBulk = (
  st: SpanTag,
  mediaIds: string[],
): Promise<APIResponse<MediaExif[]>> => {
  const url = `${config.API_URL}/v2/media/exif/v2`;

  return fetchAPI(stleaf(st, 'she8bu'), url, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: JSON.stringify({ ids: mediaIds }),
  });
};

/**
 * Fetches EXIF data for media IDs in batches to avoid API request size limitations.
 * @param st SpanTag for tracing
 * @param mediaIds Array of media IDs to fetch EXIF data for
 * @param batchSize Number of media IDs to include in each batch (default: 100)
 * @returns Combined API response with all EXIF data
 */
export const getExifBulkBatched = async (
  st: SpanTag,
  mediaIds: string[],
  batchSize = 100,
): Promise<APIResponse<MediaExif[]>> => {
  // Split mediaIds into batches
  const batches: string[][] = [];
  for (let i = 0; i < mediaIds.length; i += batchSize) {
    batches.push(mediaIds.slice(i, i + batchSize));
  }

  // Create a response object to combine results
  const combinedResponse: APIResponse<MediaExif[]> = {
    status: 200,
    body: [],
  };

  // Process each batch
  for (const batch of batches) {
    try {
      const batchResponse = await getExifBulk(stleaf(st, 'loe8bu'), batch);

      // Add batch results to the combined response
      if (batchResponse.body) {
        combinedResponse.body =
          combinedResponse.body?.concat(batchResponse.body) ||
          batchResponse.body;
      }
    } catch (error) {
      // If any batch fails, return the error status
      if (error instanceof APIError) {
        return {
          status: error.status,
          body: combinedResponse.body, // Return any successfully fetched data
        };
      }
      // For other errors, return a generic error status
      return {
        status: 500,
        body: combinedResponse.body, // Return any successfully fetched data
      };
    }
  }

  return combinedResponse;
};
